import { useCallback, useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { BookingsTab } from "@/components/admin/BookingsTab";
import { CalendarTab } from "@/components/admin/CalendarTab";
import { ListingTab } from "@/components/admin/ListingTab";
import { PhotosTab } from "@/components/admin/PhotosTab";
import { ReviewsTab } from "@/components/admin/ReviewsTab";
import { SyncTab } from "@/components/admin/SyncTab";
import { SiteTab } from "@/components/admin/SiteTab";
import { DesignTab } from "@/components/admin/DesignTab";

const TABS = [["bookings", "Reservas"], ["calendar", "Calendario"], ["site", "Sitio web"], ["design", "Diseño"], ["listing", "Anuncio y precio"], ["photos", "Fotos"], ["reviews", "Reseñas"], ["ical", "Sincronizar Airbnb"]];

export default function AdminPage() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [props, setProps] = useState([]);
  const [pid, setPid] = useState("");

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/properties");
    const arr = Array.isArray(data) ? data : [];
    setProps(arr);
    setPid((cur) => cur || arr[0]?.id || "");
  }, []);

  useEffect(() => {
    api.get("/auth/me").then((r) => { setMe(r.data); load(); }).catch(() => nav("/admin/login"));
  }, [nav, load]);

  const prop = props.find((p) => p.id === pid);
  const onSaved = (p) => setProps((arr) => arr.map((x) => (x.id === p.id ? p : x)));
  const publish = async () => {
    try {
      const { data } = await api.put(`/admin/properties/${prop.id}`, { active: true });
      onSaved(data);
      toast.success("Unidad publicada en el sitio");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    localStorage.removeItem("admin_token");
    nav("/admin/login");
  };
  const newUnit = async () => {
    const name = window.prompt("Nombre de la nueva unidad");
    if (!name) return;
    try {
      const { data } = await api.post("/admin/properties", { name });
      setProps((a) => [...a, data]);
      setPid(data.id);
      toast.success("Unidad creada. Agrégale fotos, precio y textos, y luego publícala.");
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!me) return <div className="flex min-h-screen items-center justify-center bg-[#F3EFE6] font-display text-3xl">Cargando…</div>;

  return (
    <div className="min-h-screen bg-[#F3EFE6] text-[#2C2016]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-30 border-b border-[#2C2016]/10 bg-[#FBF8F2]/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1280px] flex-wrap items-center justify-between gap-3 px-5 py-3 sm:px-8">
          <Link to="/"><Logo /></Link>
          <div className="flex flex-wrap items-center gap-2">
            <Select value={pid} onValueChange={setPid}>
              <SelectTrigger data-testid="admin-unit-select" className="w-[220px] bg-white"><SelectValue placeholder="Unidad" /></SelectTrigger>
              <SelectContent>{props.map((p) => <SelectItem key={p.id} value={p.id}><span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: p.color || "#16A34A" }} />{p.name}{p.active ? "" : " (no publicada)"}</span></SelectItem>)}</SelectContent>
            </Select>
            <button data-testid="admin-new-unit-button" onClick={newUnit} disabled={props.length >= 50} className="flex items-center gap-1 rounded-lg border border-[#2C2016]/20 bg-white px-3 py-2 text-sm hover:bg-[#2C2016]/5 disabled:opacity-40"><Plus className="h-4 w-4" />Unidad</button>
            {prop && <a href={`/stay/${prop.slug}`} target="_blank" rel="noreferrer" data-testid="admin-view-site" className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm hover:bg-[#2C2016]/5"><ExternalLink className="h-4 w-4" />Ver</a>}
            <span className="hidden text-xs text-[#4A3525] md:inline">{me.email}</span>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm hover:bg-[#2C2016]/5"><LogOut className="h-4 w-4" />Salir</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1280px] px-5 py-8 sm:px-8">
        <h1 className="flex items-center gap-3 font-display text-4xl sm:text-5xl">{prop && <i className="h-4 w-4 rounded-full" style={{ backgroundColor: prop.color || "#16A34A" }} />}{prop?.name || "—"}</h1>
        {prop && !prop.active && (
          <div data-testid="unit-unpublished-banner" className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#C4823F]/40 bg-[#C4823F]/10 p-4 text-sm">
            <span>Esta unidad aún <b>no se ve en el sitio</b>. Agrega fotos, precio y textos (pestañas Fotos y Anuncio) y luego publícala.</span>
            <button data-testid="publish-unit-button" onClick={publish} className="rounded-lg bg-[#264639] px-4 py-2 font-semibold text-white">Publicar en el sitio</button>
          </div>
        )}
        {prop && (
          <Tabs defaultValue="bookings" className="mt-6">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-[#E8D8C3]/60 p-1">
              {TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`} className="data-[state=active]:bg-[#2C2016] data-[state=active]:text-[#FBF8F2]">{l}</TabsTrigger>)}
            </TabsList>
            <TabsContent value="bookings"><BookingsTab prop={prop} /></TabsContent>
            <TabsContent value="calendar"><CalendarTab prop={prop} /></TabsContent>
            <TabsContent value="site"><SiteTab /></TabsContent>
            <TabsContent value="design"><DesignTab /></TabsContent>
            <TabsContent value="listing"><ListingTab key={prop.id} prop={prop} onSaved={onSaved} /></TabsContent>
            <TabsContent value="photos"><PhotosTab key={prop.id} prop={prop} onSaved={onSaved} /></TabsContent>
            <TabsContent value="reviews"><ReviewsTab key={prop.id} prop={prop} onSaved={onSaved} /></TabsContent>
            <TabsContent value="ical"><SyncTab key={prop.id} prop={prop} onSaved={onSaved} /></TabsContent>
          </Tabs>
        )}
      </main>
    </div>
  );
}
